import React, { memo, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useDispatch } from 'react-redux';
import { Route, Switch, HashRouter, withRouter } from 'react-router-dom';
import store from './store';

import PortfoliosController from './components/controllers/portfolios/PortfoliosController';
import MoreController from './components/controllers/more/MoreController';
import IconsController from './components/controllers/icons/IconsController';
import VersionsController from './components/controllers/versions/VersionsController';
import ContainersController from './components/controllers/containers/ContainersController';

import * as serviceWorkerRegistration from './serviceWorkerRegistration';

import { ProtectedRoute, PublicRoute } from './components/routes';
import ThemeProvider from 'components/ThemeProvider';
import LoginController from './components/pages/auth/LoginController';
import VerificationController from './components/pages/auth/VerificationController';
import ForgotPassword from './components/pages/auth/ForgotPassword';
import ResetPassword from './components/pages/auth/ResetPassword';
import UsersAdmin from './components/pages/users/admin';
import CompaniesAdmin from './components/pages/companies/admin';
import Catalogs from './components/pages/catalogs/admin';

import './forsales.min.css';
import './index.css';


import './i18n';
import { AuthApi } from 'common/api';
import { LocalStorageKey } from 'common/helpers';
import { useLocalStorage } from 'common/hooks';
import { AuthActions } from 'store/actions';
import { Role } from 'common/types/user.type';
import { useAppSelector } from 'common/hooks';
import { useTranslation } from 'react-i18next';


const App = () => {
    const app = useAppSelector((state) => state.appV2);
    const { i18n } = useTranslation();

    useEffect(() => {
        i18n.changeLanguage(app.language);
    }, [app.language, i18n]);

    return <MemoizedAppInner />;
}

const AppInner = () => {
    const dispatch = useDispatch();
    const authActions = AuthActions.useActions();
    const [token] = useLocalStorage(LocalStorageKey.Token);

    useEffect(() => {
        AuthApi.getLoggedInUser().then((user) => {
            if (!user) return;

            // Backward compatible.
            dispatch({ type: 'SET_TOKEN', payload: `Bearer ${token}` });
            dispatch({ type: 'USER_LOGIN_SUCCESS', payload: user });

            authActions.login(user);
        });
    }, [dispatch, authActions, token]);


    useLayoutEffect(() => {

        const callResize = () => {
            dispatch({ type: 'RESIZE_MAIN' });
        };

        callResize();

        setTimeout(() => callResize(), 200);
        setTimeout(() => callResize(), 1000);

        window.addEventListener("resume", callResize);
        window.addEventListener("resize", callResize);

        return () => {
            window.removeEventListener("resume", callResize);
            window.removeEventListener("resize", callResize);
        };
    }, [dispatch]);

    return (
        <ThemeProvider>
            <HashRouter>
                <Switch>
                    <ProtectedRoute path="/containers" component={withRouter(ContainersController)} />
                    <Route path="/portfolios" component={withRouter(PortfoliosController)} />
                    <Route path="/versions" component={withRouter(VersionsController)} />
                    <Route path="/icons" component={withRouter(IconsController)} />
                    <Route path="/more" component={withRouter(MoreController)} />

                    <ProtectedRoute role={Role.Admin} path="/admin/users2" component={UsersAdmin} />
                    <ProtectedRoute role={Role.Superadmin} path="/admin/companies" component={CompaniesAdmin} />
                    <ProtectedRoute role={Role.Moderator} path="/admin/catalogs" component={Catalogs} />

                    <PublicRoute path="/login" component={LoginController} />
                    <PublicRoute path="/verify-email" component={withRouter(VerificationController)} />
                    <PublicRoute path="/forgot-password" component={ForgotPassword} />
                    <PublicRoute path="/reset-password" component={ResetPassword} />

                    <Route path="/" component={withRouter(PortfoliosController)} />
                </Switch>
            </HashRouter>
        </ThemeProvider>
    );
};

const MemoizedAppInner = memo(AppInner);

const renderReactDom = () => {
    ReactDOM.render(
        <Provider store={store}>
            <App />
        </Provider>,
        document.getElementById('root')
    );
};
if (window.cordova) {
    document.addEventListener(
        'deviceready',
        () => {
            renderReactDom();
            serviceWorkerRegistration.register();
        },
        false
    );
} else {
    renderReactDom();
    serviceWorkerRegistration.register();
}
